import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

const AUTOPLAY_DELAY = 5200;

export default function ProductSlideshow({ productId, productName, image, upgradeImage }) {
  const slides = [
    { src: image, alt: `${productName} controller` },
    { src: upgradeImage, alt: `${productName} compatible upgrades, sold separately` },
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [motionAllowed, setMotionAllowed] = useState(false);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setMotionAllowed(!media.matches);
    updateMotionPreference();
    media.addEventListener?.('change', updateMotionPreference);
    return () => media.removeEventListener?.('change', updateMotionPreference);
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.15,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const updateVisibility = () => setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', updateVisibility);
    updateVisibility();
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  useEffect(() => {
    if (userPaused || hovered || focused || !visible || !pageVisible || !motionAllowed) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex(index => (index + 1) % slides.length);
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(timer);
  }, [userPaused, hovered, focused, visible, pageVisible, motionAllowed, slides.length]);

  const showSlide = (index) => {
    setActiveIndex((index + slides.length) % slides.length);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(activeIndex + 1);
    }
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(distance) > 42) showSlide(activeIndex + (distance < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <div
      ref={containerRef}
      data-testid={`gun-slideshow-${productId}`}
      data-slide-index={activeIndex}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${productName} product images`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="group/slideshow relative overflow-hidden bg-transparent aspect-square w-4/5 mx-auto flex items-center justify-center p-4 mb-[-8px] outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000]/70 rounded-xl"
    >
      <img
        key={activeIndex}
        src={slides[activeIndex].src}
        alt={slides[activeIndex].alt}
        draggable="false"
        className={`w-full h-full ${activeIndex === 0 ? 'object-cover' : 'object-contain'}`}
      />

      <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex justify-between opacity-100 transition-opacity duration-200 sm:opacity-0 sm:group-hover/slideshow:opacity-100 sm:group-focus-within/slideshow:opacity-100">
        <button
          type="button"
          aria-label={`Previous ${productName} image`}
          data-testid={`gun-slideshow-${productId}-previous`}
          onClick={() => showSlide(activeIndex - 1)}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-[#0B0C10]/85 text-white shadow-lg hover:bg-[#FF0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={`Next ${productName} image`}
          data-testid={`gun-slideshow-${productId}-next`}
          onClick={() => showSlide(activeIndex + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-[#0B0C10]/85 text-white shadow-lg hover:bg-[#FF0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-2">
        <div className="flex items-center gap-1.5 rounded-full bg-[#0B0C10]/75 px-2 py-1">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              aria-label={`Show ${productName} image ${index + 1} of ${slides.length}`}
              aria-current={index === activeIndex ? 'true' : undefined}
              data-testid={`gun-slideshow-${productId}-dot-${index}`}
              onClick={() => showSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                index === activeIndex ? 'w-4 bg-[#FF4040]' : 'w-1.5 bg-white/55 hover:bg-white'
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label={userPaused ? `Play ${productName} slideshow` : `Pause ${productName} slideshow`}
          aria-pressed={userPaused}
          data-testid={`gun-slideshow-${productId}-toggle`}
          onClick={() => setUserPaused(paused => !paused)}
          className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0B0C10]/75 text-white/90 hover:bg-[#FF0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {userPaused
            ? <Play aria-hidden="true" className="h-3 w-3" />
            : <Pause aria-hidden="true" className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
